import { format } from "date-fns";
import { CommandXFooter } from "./CommandXFooter";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface LineItem {
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  unit?: string;
}

interface InvoicePrintData {
  invoice_number: string;
  invoice_date: string;
  due_date?: string | null;
  status: string;
  total: number;
  amount_paid: number;
  payment_method?: string | null;
  payment_reference?: string | null;
  customer_name?: string;
  project_name?: string;
  company_name?: string;
  company_logo_url?: string | null;
  line_items?: LineItem[];
  expenses?: LineItem[];
  notes?: string;
}

export const InvoicePrintTemplate = ({ data }: { data: InvoicePrintData }) => {
  const serviceSubtotal = (data.line_items ?? []).reduce((s, l) => s + l.line_total, 0);
  const expenseSubtotal = (data.expenses ?? []).reduce((s, l) => s + l.line_total, 0);
  const grandTotal = data.total || serviceSubtotal + expenseSubtotal;
  const balance = grandTotal - (data.amount_paid || 0);

  const thStyle: React.CSSProperties = {
    textAlign: "left",
    padding: "10px 8px",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontWeight: 600,
    color: "#666",
    borderBottom: "2px solid #ddd",
  };

  const tdStyle: React.CSSProperties = {
    padding: "10px 8px",
    borderBottom: "1px solid #eee",
  };

  return (
    <div
      style={{
        padding: "32px",
        maxWidth: "8.5in",
        margin: "0 auto",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: "14px",
        lineHeight: 1.6,
        color: "#111",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "2px solid #ddd",
          paddingBottom: "16px",
          marginBottom: "24px",
        }}
      >
        <div>
          {data.company_logo_url && (
            <img
              src={data.company_logo_url}
              alt={data.company_name ?? "Company"}
              style={{ maxHeight: "40px", objectFit: "contain", marginBottom: "4px" }}
            />
          )}
          <h1 style={{ fontSize: "24px", fontWeight: 700, letterSpacing: "-0.025em", color: "#111", margin: 0 }}>
            INVOICE
          </h1>
          {data.company_name && (
            <p style={{ fontSize: "16px", fontWeight: 500, marginTop: "4px", color: "#333" }}>{data.company_name}</p>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <p style={{ fontSize: "18px", fontWeight: 700, fontFamily: "monospace", color: "#111", margin: 0 }}>
            {data.invoice_number}
          </p>
          <p style={{ color: "#666", margin: "2px 0" }}>
            Date: {format(new Date(data.invoice_date), "MMMM d, yyyy")}
          </p>
          {data.due_date && (
            <p style={{ color: "#666", margin: "2px 0" }}>
              Due: {format(new Date(data.due_date), "MMMM d, yyyy")}
            </p>
          )}
        </div>
      </div>

      {/* Bill To / Project */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px", marginBottom: "32px" }}>
        <div>
          <p style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600, color: "#999", marginBottom: "4px" }}>
            Bill To
          </p>
          <p style={{ fontWeight: 500, color: "#111", margin: 0 }}>{data.customer_name || "—"}</p>
        </div>
        {data.project_name && (
          <div>
            <p style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600, color: "#999", marginBottom: "4px" }}>
              Project
            </p>
            <p style={{ fontWeight: 500, color: "#111", margin: 0 }}>{data.project_name}</p>
          </div>
        )}
      </div>

      {/* Services Line Items */}
      {data.line_items && data.line_items.length > 0 && (
        <>
          <p style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600, color: "#999", marginBottom: "8px" }}>
            Services
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "8px" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "50%" }}>Description</th>
                <th style={{ ...thStyle, width: "15%", textAlign: "right" }}>Qty</th>
                <th style={{ ...thStyle, width: "15%", textAlign: "right" }}>Unit Price</th>
                <th style={{ ...thStyle, width: "20%", textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.line_items.map((li, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{li.description}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    {new Intl.NumberFormat("en-US").format(li.quantity)}{li.unit ? ` ${li.unit}` : ""}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    {fmt(li.unit_price)}{li.unit ? `/${li.unit}` : ""}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", fontWeight: 500 }}>{fmt(li.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.expenses && data.expenses.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", width: "260px", padding: "4px 0", borderBottom: "1px solid #eee" }}>
                <span style={{ color: "#666" }}>Services Subtotal</span>
                <span style={{ fontWeight: 600 }}>{fmt(serviceSubtotal)}</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Expenses */}
      {data.expenses && data.expenses.length > 0 && (
        <>
          <p style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600, color: "#999", marginBottom: "8px", marginTop: "16px" }}>
            Expenses & Reimbursements
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "8px" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: "left" }}>Description</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.expenses.map((ex, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{ex.description}</td>
                  <td style={{ ...tdStyle, textAlign: "right", fontWeight: 500 }}>{fmt(ex.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", width: "260px", padding: "4px 0", borderBottom: "1px solid #eee" }}>
              <span style={{ color: "#666" }}>Expenses Subtotal</span>
              <span style={{ fontWeight: 600 }}>{fmt(expenseSubtotal)}</span>
            </div>
          </div>
        </>
      )}

      {/* Notes */}
      {data.notes && (
        <div style={{ marginBottom: "24px", padding: "12px", borderRadius: "4px", backgroundColor: "#f9f9f9", border: "1px solid #eee" }}>
          <p style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600, color: "#999", marginBottom: "4px" }}>
            Notes
          </p>
          <p style={{ color: "#333", whiteSpace: "pre-wrap", margin: 0 }}>{data.notes}</p>
        </div>
      )}

      {/* Totals */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <div style={{ width: "260px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #eee" }}>
            <span style={{ color: "#666" }}>Total</span>
            <span style={{ fontWeight: 600 }}>{fmt(grandTotal)}</span>
          </div>
          {data.amount_paid > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #eee" }}>
              <span style={{ color: "#666" }}>Paid</span>
              <span style={{ fontWeight: 600, color: "#16a34a" }}>({fmt(data.amount_paid)})</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "16px", fontWeight: 700, borderTop: "2px solid #111" }}>
            <span>Balance Due</span>
            <span>{fmt(balance)}</span>
          </div>
        </div>
      </div>

      {/* Payment info */}
      {data.payment_method && (
        <div style={{ marginTop: "24px", paddingTop: "16px", borderTop: "1px solid #eee" }}>
          <p style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600, color: "#999", marginBottom: "4px" }}>
            Payment Info
          </p>
          <p style={{ margin: "2px 0" }}>
            Method: <span style={{ textTransform: "capitalize" }}>{data.payment_method.replace("_", " ")}</span>
          </p>
          {data.payment_reference && <p style={{ margin: "2px 0" }}>Reference: {data.payment_reference}</p>}
        </div>
      )}

      <CommandXFooter />
    </div>
  );
};
